/**
 * Template Health hub — a single Card joining the four existing tag-health
 * surfaces (activation validation, docx↔schema audit, clause library, body
 * tag inventory) behind one header badge, so an admin can see everything
 * that needs attention on a template in one place instead of hunting across
 * TemplateDetail and ClauseManager. Counts are sourced from
 * `aggregateTemplateHealth` (frontend/src/utils/templateHealth.ts); this
 * component owns only the clause-tag parse (run when clauses have loaded)
 * and the presentation — no tag parsing of its own.
 */

import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Card, Badge } from '../common';
import TagSummaryStrip from './TagSummaryStrip';
import SchemaTagAuditPanel from './SchemaTagAuditPanel';
import UnusedTagsPanel from './UnusedTagsPanel';
import BodyTagsViewer from './BodyTagsViewer';
import { aggregateTemplateHealth, countOrphanControlTags } from '../../utils/templateHealth';
import { parseClauseTags, findOrphanedClauseIds, type ClauseTagRef } from '../../utils/clauseTagParser';
import type { OrgTemplateSchema, TagValidation } from '../../types/orgTemplate';

interface TemplateHealthPanelProps {
  bodyTags: string[] | undefined;
  schema: OrgTemplateSchema | undefined;
  tagValidation: TagValidation | undefined;
  clauseRefs: ClauseTagRef[] | undefined; // undefined while clauses load
  clausesLoaded: boolean;
  onAddClauseFromTag: (tag: string) => void; // host navigates to ClauseManager deep link
}

export default function TemplateHealthPanel({
  bodyTags,
  schema,
  tagValidation,
  clauseRefs,
  clausesLoaded,
  onAddClauseFromTag,
}: TemplateHealthPanelProps) {
  const [isBodyTagsExpanded, setIsBodyTagsExpanded] = useState(false);

  const hasBodyTags = !!bodyTags && bodyTags.length > 0;

  // Only run the clause-tag parse once clauses have loaded — passing
  // `parse: undefined` to the aggregator keeps a still-loading clause
  // library out of the header badge total.
  const parse = useMemo(() => {
    if (!clausesLoaded || !bodyTags || !clauseRefs) return undefined;
    return parseClauseTags(bodyTags, clauseRefs);
  }, [clausesLoaded, bodyTags, clauseRefs]);

  const orphanedClauseIds = useMemo(() => {
    if (!parse || !clauseRefs) return undefined;
    return findOrphanedClauseIds(
      parse.referencedClauseIds,
      clauseRefs.map((c) => c.id)
    );
  }, [parse, clauseRefs]);

  const counts = aggregateTemplateHealth({ bodyTags, schema, tagValidation, parse, orphanedClauseIds });
  const bodyTagOrphanCount = countOrphanControlTags(bodyTags);

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-h3 text-slate-900 dark:text-slate-100">Template Health</h2>
        {hasBodyTags &&
          (counts.total > 0 ? (
            <Badge variant="pending" label={`${counts.total} issue${counts.total === 1 ? '' : 's'}`} />
          ) : (
            <Badge variant="active" label="Healthy" />
          ))}
      </div>

      {!hasBodyTags ? (
        <p className="text-body-sm text-slate-500 dark:text-slate-400">
          No template version uploaded yet — health checks run once a version is active.
        </p>
      ) : (
        <div className="space-y-6">
          <section>
            <h3 className="text-body-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Activation validation
            </h3>
            {tagValidation && (
              <div className="mb-3">
                <TagSummaryStrip summary={tagValidation.summary} sourceVersion={tagValidation.sourceVersion} />
              </div>
            )}
            {tagValidation && tagValidation.warnings.length > 0 && (
              <div
                role="status"
                className="rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-body-sm text-amber-800 dark:text-amber-200"
              >
                <p className="font-medium mb-1">Clause markup warnings from the last activation</p>
                <ul className="list-disc pl-5 space-y-1">
                  {tagValidation.warnings.map((warning, i) => (
                    <li key={`${warning.code}-${warning.tag ?? i}`}>
                      {warning.tag && <code className="font-mono">{`{${warning.tag}}`}</code>}
                      {warning.tag ? ' — ' : ''}
                      {warning.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          {schema && (
            <section>
              <h3 className="text-body-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Schema audit</h3>
              <SchemaTagAuditPanel bodyTags={bodyTags} schema={schema} />
            </section>
          )}

          <section>
            <h3 className="text-body-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Clause library</h3>
            {!clausesLoaded ? (
              <p className="text-body-sm text-slate-500 dark:text-slate-400">Checking clause library…</p>
            ) : (
              <UnusedTagsPanel
                unknownTags={parse?.unknownTags ?? []}
                ambiguousTags={parse?.ambiguousTags ?? []}
                orphanedClauseIds={orphanedClauseIds ?? []}
                onAddClause={onAddClauseFromTag}
              />
            )}
          </section>

          <section className="rounded-lg border border-slate-200 dark:border-slate-700">
            <button
              onClick={() => setIsBodyTagsExpanded(!isBodyTagsExpanded)}
              className="flex w-full items-center justify-between p-4 text-left"
              aria-expanded={isBodyTagsExpanded}
            >
              <span className="flex items-center gap-2 text-body-sm font-medium text-slate-700 dark:text-slate-300">
                Body tag inventory ({bodyTags.length})
                {bodyTagOrphanCount > 0 && (
                  <span className="text-caption text-amber-700 dark:text-amber-400">
                    {bodyTagOrphanCount} orphaned
                  </span>
                )}
              </span>
              {isBodyTagsExpanded ? (
                <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
              )}
            </button>
            {isBodyTagsExpanded && (
              <div className="border-t border-slate-200 dark:border-slate-700 p-4">
                <BodyTagsViewer bodyTags={bodyTags} />
              </div>
            )}
          </section>
        </div>
      )}
    </Card>
  );
}
